/**
 * ReportContents — the contents block, shared by the filed detail and the preview.
 *
 * One renderer on purpose: a preview that displayed its contents differently
 * from the filed report would invite exactly the doubt this screen exists to
 * remove — the two come from the same projection and must look the same.
 *
 * Remove buttons show only when `canCurate` is set, which the preview does while
 * editing a filed report, and only on sections marked curatable.
 */

import { AuthenticatedPhoto } from "./AuthenticatedPhoto";
import { occurrenceId } from "../reports/report-labels";
import type { ReportIncludedItem, ReportSidecar } from "../reports/report-types";

export interface ReportContentsProps {
  /** Test-id prefix: "report-preview" or "report-filed-detail". */
  prefix: string;
  heading: string;
  /** Shown when the contents could not be read — never as "it was empty". */
  whereFallback: string;
  sidecar: ReportSidecar | null;
  sidecarError: string | null;
  onOpenItem?: (capture_id: string) => void;
  fetchPhoto?: (media_ref: string) => Promise<Blob | null>;
  canCurate?: boolean;
  /** The occurrence being changed, or "__additions__"; disables every Remove. */
  curationBusy?: string | null;
  onRemove?: (item: ReportIncludedItem) => void;
}

export function ReportContents({
  prefix,
  heading,
  whereFallback,
  sidecar,
  sidecarError,
  onOpenItem,
  fetchPhoto,
  canCurate = false,
  curationBusy = null,
  onRemove,
}: ReportContentsProps) {
  return (
    <div class="report-detail-included" data-testid={`${prefix}-included`}>
      <p class="save-chip-group-hint">{heading}</p>
      {sidecarError !== null ? (
        /* NOT "it was empty". Those are different answers and only one of them
           is a reason to go looking elsewhere. */
        <p class="report-detail-meta" data-testid={`${prefix}-items-unavailable`}>
          {whereFallback}
        </p>
      ) : sidecar === null ? (
        <p class="report-detail-meta" data-testid={`${prefix}-items-loading`}>
          Loading…
        </p>
      ) : sidecar.included.length === 0 && (sidecar.sections?.length ?? 0) === 0 ? (
        <p class="report-detail-meta" data-testid={`${prefix}-items-none`}>
          Nothing is in this one yet.
        </p>
      ) : (
        /* Grouped as the REPORT is grouped. A flat list would file an open
           action under the same heading as completed work — and labelling one
           section "Daily logs" is what made a report carrying 8 open actions
           say it had nothing in it. */
        <>
        {sidecar.header && (
          <header class="report-native-header" data-testid={`${prefix}-report-header`}>
            <p class="report-native-kicker">{sidecar.header.audience} · {sidecar.header.title}</p>
            <h2>{sidecar.header.project}</h2>
            <p>{sidecar.header.reportingWindow} · {sidecar.header.timezone}</p>
            {sidecar.header.details.length > 0 && <p>{sidecar.header.details.join(" · ")}</p>}
          </header>
        )}
        {(sidecar.sections ?? [{ label: "", items: sidecar.included, curatable: false }]).map((sec) => (
          <div key={sec.label} data-testid={`${prefix}-section-${sec.label || "all"}`}>
            {sec.label !== "" && <h3 class="report-native-section-title">{sec.label}</h3>}
            {sec.note && <p class="report-detail-meta">{sec.note}</p>}
            {sec.items.length > 0 && <ul data-testid={`${prefix}-items`}>
              {sec.items.map((it) => (
                <li key={occurrenceId(it)} data-occurrence-id={occurrenceId(it)} data-testid={`${prefix}-item-${it.capture_id}`}>
                  {it.openable !== false ? <button
                    type="button"
                    class="report-item-open"
                    onClick={() => onOpenItem?.(it.capture_id)}
                  >
                    <span class="report-item-title">{it.title}</span>
                    <span class="report-item-meta">{it.meta}</span>
                    <span class="report-item-chevron" aria-hidden="true">›</span>
                  </button> : <div class="report-item-text">
                    <span class="report-item-title">{it.title}</span>
                    <span class="report-item-meta">{it.meta}</span>
                  </div>}
                  {(it.photos ?? []).map((mediaRef, index) => (
                    <AuthenticatedPhoto
                      key={mediaRef}
                      mediaRef={mediaRef}
                      alt={`Photo attached to ${it.title}`}
                      testid={`${prefix}-photo-${it.capture_id}-${index}`}
                      fetchPhoto={fetchPhoto}
                      className="report-item-photo"
                    />
                  ))}
                  {canCurate && sec.curatable === true && (
                    <button
                      type="button"
                      class="report-item-remove"
                      data-testid={`${prefix}-remove-${it.capture_id}`}
                      disabled={curationBusy !== null}
                      onClick={() => onRemove?.(it)}
                    >
                      {curationBusy === occurrenceId(it) ? "Removing…" : "Remove from this report"}
                    </button>
                  )}
                </li>
              ))}
            </ul>}
          </div>
        ))}
        </>
      )}
    </div>
  );
}
